import type { Schueler, Fehlzeiten } from '../types';

export const FEHLZEITEN_GRENZEN_KEY = 'lehrerdesk_fehlzeiten_grenzen';

// Welle D3b: die Heute-Widget-Auswahl (lehrerdesk-widgets) lebt im Board-Layout auf dem Server;
// der alte Schlüssel wird einmalig von features/heute/board/useBoard.ts übernommen.

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = window.atob(base64);
  const arr     = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function ladeFehlzeitenGrenzen(): { achtung: number; kritisch: number } {
  try {
    const r = localStorage.getItem(FEHLZEITEN_GRENZEN_KEY);
    const g = r ? JSON.parse(r) : null;
    return { achtung: g?.achtung ?? 10, kritisch: g?.kritisch ?? 20 };
  } catch { return { achtung: 10, kritisch: 20 }; }
}

// Welle U2: EINE Ampel-Regel für alle Ansichten, identisch mit dem Backend
// (dashboard.js, Insights fehlzeit_rot/gelb): unentschuldigte Stunden gegen die
// Schwellen der Lehrkraft (fz_schwelle_gelb/rot, beim Start in localStorage
// gecacht). 10/20 greifen nur als Rückfall, solange keine Einstellung geladen ist.
export function fzStufe(unentschuldigt: number): 'kritisch' | 'achtung' | 'ok' {
  const grenzen = ladeFehlzeitenGrenzen();
  if (unentschuldigt >= grenzen.kritisch) return 'kritisch';
  if (unentschuldigt >= grenzen.achtung)  return 'achtung';
  return 'ok';
}

export function ampelFarbe(fehlstunden: number, entschuldigt: number): string {
  const st = fzStufe(fehlstunden - entschuldigt);
  return st === 'kritisch' ? '#FF6B6B' : st === 'achtung' ? '#B5790F' : '#4ECDC4';
}

export function ampelLabel(fehlstunden: number, entschuldigt: number): string {
  const st = fzStufe(fehlstunden - entschuldigt);
  return st === 'kritisch' ? '§68 HSchG' : st === 'achtung' ? 'Achtung' : 'OK';
}

/** Formatiert Fehlstunden als "X Std. (Y Tag(e) + Z Std.)" wenn ≥ 6 Std. */
export function formatFehlzeiten(stunden: number): string {
  if (stunden <= 0) return '0 Std.';
  if (stunden < 6)  return `${stunden} Std.`;
  const tage = Math.floor(stunden / 6);
  const rest  = stunden % 6;
  if (rest === 0) return `${stunden} Std. (${tage} ${tage === 1 ? 'Tag' : 'Tage'})`;
  return `${stunden} Std. (${tage} ${tage === 1 ? 'Tag' : 'Tage'} + ${rest} Std.)`;
}

export function notenFarbe(n: number | string): string {
  const v = typeof n === 'string' ? parseFloat(n) : n;
  if (v <= 1.5) return '#4ECDC4';
  if (v <= 2.5) return '#7C5CBF';
  if (v <= 3.5) return '#B5790F';
  if (v <= 4.5) return '#B5790F';
  return '#FF6B6B';
}

/** §26 HSchG / HKM – Notenfarbe nach Ampelschema (1–2 grün, 3 gelb, 4–6 rot) */
export function notenFarbeKMK(n: number): string {
  if (n <= 2.5) return '#4ECDC4';
  if (n <= 3.5) return '#B5790F';
  return '#FF6B6B';
}

/** Hessen-Gewichtung Deutsch: 50 % Klassenarbeiten, 50 % Mündlich */
export function berechneNote(ka1: number, ka2: number, ka3: number, ka4: number, m: number): string {
  const ka = (ka1 + ka2 + ka3 + ka4) / 4;
  const g  = ka * 0.5 + m * 0.5;
  if (g <= 1.17) return '1+';
  if (g <= 1.50) return '1';
  if (g <= 1.83) return '1-';
  if (g <= 2.17) return '2+';
  if (g <= 2.50) return '2';
  if (g <= 2.83) return '2-';
  if (g <= 3.17) return '3+';
  if (g <= 3.50) return '3';
  if (g <= 3.83) return '3-';
  if (g <= 4.17) return '4+';
  if (g <= 4.50) return '4';
  if (g <= 4.83) return '4-';
  if (g <= 5.17) return '5+';
  if (g <= 5.50) return '5';
  if (g <= 5.83) return '5-';
  return '6';
}

// ── Avatar-Farben ─────────────────────────────────────────────────────────
export const AVATAR_COLORS = [
  { bg: '#EEECFB', fg: '#534AB7' },
  { bg: '#E1F5EE', fg: '#0F6E56' },
  { bg: '#FAECE7', fg: '#993C1D' },
  { bg: '#FBEAF0', fg: '#72243E' },
  { bg: '#FAEEDA', fg: '#854F0B' },
  { bg: '#E6F1FB', fg: '#0C447C' },
  { bg: '#EAF3DE', fg: '#27500A' },
];

export function avatarColor(key: string | number): { bg: string; fg: string } {
  let h = 0;
  for (const c of String(key)) h = (h * 31 + c.charCodeAt(0)) & 0xffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

// ── Fehlzeiten-Accessor-Kurzformen ────────────────────────────────────────
export const fzUnen   = (sc: Schueler) => sc.fehlzeiten.stunden_unentschuldigt;
export const fzEntsch = (sc: Schueler) => sc.fehlzeiten.stunden_gesamt - sc.fehlzeiten.stunden_unentschuldigt;
export const fzText   = (fz: Fehlzeiten) =>
  `${fz.tage_gesamt} Tage (${fz.tage_unentschuldigt} unentsch.) · ${fz.stunden_gesamt} Std. (${fz.stunden_unentschuldigt} unentsch.)`;
